import React, { useState } from "react";
import { useOutletContext } from "react-router-dom";
import { toast } from "sonner";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button, Card } from "@/components/ui-kit";
import { gradeLabel, GRADE_OPTIONS } from "@/config";
import { Contrast, Type, Clock, GraduationCap } from "lucide-react";

export default function Settings() {
  const { student, reload, studentId } = useOutletContext();
  const { user } = useAuth();
  const [settings, setSettings] = useState(student.settings || {});
  const [grade, setGrade] = useState(student.grade);
  const [budget, setBudget] = useState(student.daily_time_budget);
  const canManage = user.role !== "teacher";

  const patch = async (payload, msg) => {
    await api.patch(`/students/${studentId}`, payload);
    await reload();
    if (msg) toast.success(msg);
  };

  const toggle = (key) => {
    const next = { ...settings, [key]: !settings[key] };
    setSettings(next);
    patch({ settings: next });
  };

  return (
    <div>
      <h1 className="font-head text-3xl font-bold mb-5">Settings</h1>

      <Card className="p-5 mb-4">
        <div className="text-xs font-semibold uppercase tracking-wider text-[var(--ns-subtle)] mb-3">Accessibility</div>
        <Toggle icon={Contrast} label="High-contrast mode" desc="Dark, high-contrast palette (WCAG AAA)."
          checked={!!settings.high_contrast} onChange={() => toggle("high_contrast")} testid="toggle-hc" />
        <Toggle icon={Type} label="Dyslexia-friendly font" desc="Lexend with wider spacing and taller lines."
          checked={!!settings.dyslexia_font} onChange={() => toggle("dyslexia_font")} testid="toggle-dys" />
      </Card>

      {canManage && (
        <Card className="p-5 mb-4">
          <div className="text-xs font-semibold uppercase tracking-wider text-[var(--ns-subtle)] mb-3">Study plan</div>
          <div className="grid gap-4">
            <div>
              <label className="flex items-center gap-2 text-sm font-semibold mb-2"><Clock size={16} /> Daily time budget</label>
              <div className="flex gap-2">
                {[10, 15, 20, 30].map((m) => (
                  <button key={m} data-testid={`budget-${m}`} onClick={() => { setBudget(m); patch({ daily_time_budget: m }, "Time budget updated"); }}
                    className="flex-1 rounded-[var(--ns-radius)] border-2 py-2 text-sm font-semibold"
                    style={{ borderColor: budget === m ? "var(--ns-primary)" : "var(--ns-border)", background: budget === m ? "var(--ns-primary-light)" : "transparent", color: budget === m ? "var(--ns-primary-hover)" : "var(--ns-muted)" }}>
                    {m}m
                  </button>
                ))}
              </div>
            </div>
            {user.role !== "student" && (
              <div>
                <label className="flex items-center gap-2 text-sm font-semibold mb-2"><GraduationCap size={16} /> Grade level</label>
                <select data-testid="grade-setting" value={grade} onChange={(e) => { const g = Number(e.target.value); setGrade(g); patch({ grade: g }, "Grade updated"); }}
                  className="w-full rounded-[var(--ns-radius)] border-2 border-[var(--ns-border)] bg-[var(--ns-surface)] px-3 py-3">
                  {GRADE_OPTIONS.map((g) => <option key={g} value={g}>{gradeLabel(g)}</option>)}
                </select>
              </div>
            )}
          </div>
        </Card>
      )}

      <Card className="p-5">
        <div className="text-xs font-semibold uppercase tracking-wider text-[var(--ns-subtle)] mb-2">Consent</div>
        <p className="text-sm text-[var(--ns-muted)]">
          {student.consent?.granted ? "Parent/guardian consent is on file for this account." : "Consent not yet recorded."}
          {student.self_managed && " This is a self-managed (13+) student account."}
        </p>
      </Card>
    </div>
  );
}

function Toggle({ icon: Icon, label, desc, checked, onChange, testid }) {
  return (
    <div className="flex items-center justify-between py-3 border-b border-[var(--ns-border)] last:border-0">
      <div className="flex items-start gap-3">
        <Icon size={20} className="text-[var(--ns-primary)] mt-0.5" />
        <div>
          <div className="font-semibold text-sm">{label}</div>
          <div className="text-xs text-[var(--ns-muted)]">{desc}</div>
        </div>
      </div>
      <button data-testid={testid} onClick={onChange} className="w-12 h-7 rounded-full transition-colors shrink-0"
        style={{ background: checked ? "var(--ns-primary)" : "var(--ns-border)" }}>
        <span className="block w-5 h-5 bg-white rounded-full transition-transform" style={{ transform: checked ? "translateX(22px)" : "translateX(3px)" }} />
      </button>
    </div>
  );
}
